import axios from 'axios';
import { authApi, readUserSession } from '../../Auth/userSession';

const publicApi = axios.create({ baseURL: (process.env.REACT_APP_SERVER || '').replace(/\/$/, ''), timeout: 45000 });

export const LOGIN_REQUIRED_MESSAGE = 'AI 내담자는 로그인 후 이용할 수 있습니다. 로그인해주세요.';

function requireLogin() {
    if (!readUserSession()) throw new Error(LOGIN_REQUIRED_MESSAGE);
}

// 내담자 준비 정보(instance, token)는 면담 조회 API가 없어 이 브라우저에 보관한다. 대화는 모두 서버 API를 사용한다.
const instanceKey = 'garam-ai-client-demo-instances-v1';
const read = key => JSON.parse(localStorage.getItem(key) || '{}');
const write = (key, value) => localStorage.setItem(key, JSON.stringify(value));

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

const PREPARE_ERROR_MESSAGES = {
    400: '면담 설정을 확인해주세요. 면담 시간은 15~20분이어야 합니다.',
    401: LOGIN_REQUIRED_MESSAGE,
    404: '선택한 내담자 정보를 찾을 수 없습니다.',
    409: '이미 진행 중인 면담이 있습니다. 진행 중인 면담을 마친 뒤 다시 준비해주세요.',
};

const SERVER_ERROR_MESSAGE = '서버에 문제가 생겼어요. 문의하기에서 문의주세요.';

// 보안상 서버 오류 내용은 화면에 보여주지 않는다. 개발 중에만 콘솔에 남긴다(프로덕션 빌드에서는 제거된다).
function apiErrorMessage(error, messages) {
    if (process.env.NODE_ENV !== 'production') console.error(error);
    if (axios.isCancel(error)) return LOGIN_REQUIRED_MESSAGE;
    const status = error.response?.status;
    if (messages[status]) return messages[status];
    if (!error.response) return '서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.';
    return SERVER_ERROR_MESSAGE;
}

// 공개 내담자 목록. 로그인 없이 조회한다.
export async function getClients() {
    let data;
    try {
        ({ data } = await publicApi.get('/api/ai-clients/profiles'));
    } catch (error) {
        throw new Error(apiErrorMessage(error, {}));
    }
    if (!Array.isArray(data?.clients)) {
        if (process.env.NODE_ENV !== 'production') console.error('내담자 목록 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return { clients: data.clients };
}

// 공개 내담자 상세 프로필.
export async function getClientProfile(id) {
    let data;
    try {
        ({ data } = await publicApi.get(`/api/ai-clients/profiles/${encodeURIComponent(id)}`));
    } catch (error) {
        throw new Error(apiErrorMessage(error, { 404: '선택한 내담자 정보를 찾을 수 없습니다.' }));
    }
    if (!data?.id) {
        if (process.env.NODE_ENV !== 'production') console.error('내담자 상세 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return data;
}

// 서버에 면담 인스턴스를 만들고 { token(X-Instance-Token용), instance }를 돌려준다.
export async function prepareClient({ profileId, durationMinutes, supervisionModel }) {
    let data;
    try {
        ({ data } = await authApi.post('/api/ai-clients/instances', { profileId, durationMinutes, supervisionModel }));
    } catch (error) {
        throw new Error(apiErrorMessage(error, PREPARE_ERROR_MESSAGES));
    }
    if (!/^[0-9a-f]{64}$/i.test(data?.token || '') || !data?.instance?.id) {
        if (process.env.NODE_ENV !== 'production') console.error('면담 준비 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    // 응답에 사례 정보가 없으면 화면 표시용으로 상세 프로필을 붙인다.
    const instance = { ...data.instance, client: data.instance.client || await getClientProfile(profileId) };
    // 새로고침 후에도 2단계 화면에서 사례 정보를 보여주기 위해 보관한다.
    const instances = read(instanceKey);
    instances[instance.id] = instance;
    write(instanceKey, instances);
    return { token: data.token, instance: clone(instance) };
}

export async function getPreparedClient(id) {
    requireLogin();
    const instance = read(instanceKey)[id];
    if (!instance) throw new Error('저장된 면담 정보를 찾을 수 없습니다. 내담자를 다시 준비해주세요.');
    return clone(instance);
}

const CHAT_ERROR_MESSAGES = {
    401: LOGIN_REQUIRED_MESSAGE,
    403: '면담 정보를 확인할 수 없습니다. 내담자 준비 단계에서 다시 준비해주세요.',
    404: '면담 정보를 찾을 수 없습니다. 내담자 준비 단계에서 다시 준비해주세요.',
};
const START_ERROR_MESSAGES = { ...CHAT_ERROR_MESSAGES, 429: '오늘 시작할 수 있는 면담 3회를 모두 사용했습니다. 내일 다시 시도해주세요.' };
const SEND_ERROR_MESSAGES = { ...CHAT_ERROR_MESSAGES, 400: '메시지를 확인해주세요. 2,000자 이내로 입력할 수 있습니다.' };
const CHAT_ENDED_MESSAGE = '종료된 면담에는 메시지를 보낼 수 없습니다.';

function validChat(data) {
    if (!['prepared', 'active', 'completed'].includes(data?.status) || !Array.isArray(data.messages) || typeof data.serverNow !== 'number') return false;
    return data.status !== 'active' || typeof data.expiresAt === 'number';
}

// 대화 API 공통 호출. Authorization은 authApi가, X-Instance-Token은 여기서 붙인다.
async function chatRequest(method, id, token, path, body, messages, label) {
    let data;
    try {
        ({ data } = await authApi.request({
            method, url: `/api/ai-clients/instances/${encodeURIComponent(id)}/chat${path}`,
            data: body, headers: { 'X-Instance-Token': token },
        }));
    } catch (error) {
        // 이미 끝난 면담에 보낸 경우: 화면이 최신 상태를 다시 불러오도록 코드를 붙인다.
        if (method === 'post' && path === '/messages' && error.response?.status === 409) {
            if (process.env.NODE_ENV !== 'production') console.error(error);
            const failure = new Error(CHAT_ENDED_MESSAGE);
            failure.code = 'CHAT_ENDED';
            throw failure;
        }
        throw new Error(apiErrorMessage(error, messages));
    }
    if (!validChat(data)) {
        if (process.env.NODE_ENV !== 'production') console.error(`${label} 응답 형식 오류`, data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    // 내담자 준비 화면이 진행 중 여부를 알 수 있도록 보관한 instance 상태를 맞춘다.
    try {
        const instances = read(instanceKey);
        if (instances[id]) { instances[id].status = data.status; write(instanceKey, instances); }
    } catch { /* 보관 실패는 면담 진행에 영향이 없다. */ }
    return data;
}

// 대화 상태·목록 조회만 한다.
export const getChat = (id, token) => chatRequest('get', id, token, '', undefined, CHAT_ERROR_MESSAGES, '대화 조회');

// 한 번 시작(하루 3회): 오프닝을 넣고 서버 시계를 켠다.
export const startChat = (id, token) => chatRequest('post', id, token, '/start', {}, START_ERROR_MESSAGES, '면담 시작');

// 메시지 보내기. turnId가 같으면 서버가 중복 전송으로 처리한다.
export const sendChatMessage = (id, token, { turnId, text }) => chatRequest('post', id, token, '/messages', { turnId, text }, SEND_ERROR_MESSAGES, '메시지 전송');

// 한 번 수동 종료.
export const finishChat = (id, token) => chatRequest('post', id, token, '/finish', {}, CHAT_ERROR_MESSAGES, '면담 종료');

// 가장 최근에 준비한 면담의 참여 정보({ id, token }). 내담자 준비 화면이 저장한다.
export const PREPARED_INSTANCE_KEY = 'garam-ai-client-instance-v1';

export function readPreparedInstance() {
    try {
        const saved = JSON.parse(localStorage.getItem(PREPARED_INSTANCE_KEY) || 'null');
        return saved?.id && saved?.token ? saved : null;
    } catch {
        return null;
    }
}

const FEEDBACK_ERROR_CODES = {
    SUPERVISION_MODEL_UNSUPPORTED: '현재 종합 피드백은 동기면담(MI) 관점으로 준비한 면담만 받을 수 있습니다.',
    CHAT_NOT_COMPLETED: '면담을 마친 뒤 종합 피드백을 받을 수 있습니다.',
};

// MI 종합 피드백 분석·저장. 이미 분석한 면담이면 서버가 저장된 결과를 돌려준다.
export async function requestFeedback(id, token) {
    try {
        const { data } = await authApi.post(`/api/ai-clients/instances/${encodeURIComponent(id)}/analysis/feedback`, {}, {
            headers: { 'X-Instance-Token': token },
            // 분석에 시간이 걸릴 수 있어 기본보다 길게 기다린다.
            timeout: 180000,
        });
        if (!data?.strengthsImprovements) {
            if (process.env.NODE_ENV !== 'production') console.error('종합 피드백 응답 형식 오류', data);
            throw new Error(SERVER_ERROR_MESSAGE);
        }
        return data;
    } catch (error) {
        if (!error.isAxiosError && !axios.isCancel(error)) throw error;
        // 오류 코드는 화면 문구를 고르는 데만 쓰고 그대로 보여주지 않는다.
        const code = error.response?.data?.code || error.response?.data?.error;
        if (FEEDBACK_ERROR_CODES[code]) {
            if (process.env.NODE_ENV !== 'production') console.error(error);
            throw new Error(FEEDBACK_ERROR_CODES[code]);
        }
        throw new Error(apiErrorMessage(error, CHAT_ERROR_MESSAGES));
    }
}

// 화면의 항목 키와 서버 항목 키(field) 대응표.
const CONCEPTUALIZATION_FIELD_KEYS = { presenting: 'presenting', changetalk: 'changeTalk', risk: 'risk', plan: 'goalsNext' };

const CONCEPTUALIZATION_ERROR_MESSAGES = {
    ...CHAT_ERROR_MESSAGES,
    400: '작성한 내용을 확인해주세요. 1~2,000자로 입력할 수 있습니다.',
    429: '피드백 요청이 많습니다. 잠시 후 다시 시도해주세요.',
};
const CONCEPTUALIZATION_ERROR_CODES = {
    CHAT_NOT_COMPLETED: '면담을 마친 뒤 사례개념화 피드백을 받을 수 있습니다.',
    ANALYSIS_NOT_FOUND: '아직 서버에 저장한 항목이 없습니다. 항목을 작성하고 저장한 뒤 점검을 받아주세요.',
};

// 서버 항목 키로 된 객체를 화면 항목 키로 바꾼다. 모르는 키는 버린다.
function toLocalFieldKeys(serverValues) {
    return Object.fromEntries(Object.entries(CONCEPTUALIZATION_FIELD_KEYS)
        .filter(([, serverKey]) => serverValues?.[serverKey] !== undefined)
        .map(([localKey, serverKey]) => [localKey, serverValues[serverKey]]));
}

// 항목 피드백 하나가 화면에 그릴 수 있는 형식인지 확인한다.
function validFieldFeedback(feedback) {
    return typeof feedback?.label === 'string' && typeof feedback.note === 'string';
}

// 항목 피드백의 목록 값을 배열로 맞춘다. 서버가 빼먹어도 화면이 깨지지 않게 한다.
function normalizeFieldFeedback(feedback) {
    return {
        ...feedback,
        guideReminders: Array.isArray(feedback.guideReminders) ? feedback.guideReminders.filter(item => typeof item === 'string') : [],
        revealSummary: typeof feedback.revealSummary === 'string' ? feedback.revealSummary : '',
        evidenceMessageIndexes: Array.isArray(feedback.evidenceMessageIndexes) ? feedback.evidenceMessageIndexes.filter(Number.isInteger) : [],
    };
}

// 사례개념화 응답을 화면 키 기준으로 바꾼다. 복원 응답에는 fieldFeedback이 없어 null, 연결 점검(coherence)이 없거나 형식이 다르면 null이 된다.
export function toConceptualizationResult(data) {
    const fields = Object.fromEntries(Object.entries(toLocalFieldKeys(data.fields))
        .filter(([, feedback]) => validFieldFeedback(feedback))
        .map(([key, feedback]) => [key, normalizeFieldFeedback(feedback)]));
    const fieldKey = Object.keys(CONCEPTUALIZATION_FIELD_KEYS).find(key => CONCEPTUALIZATION_FIELD_KEYS[key] === data.fieldFeedback?.key);
    const fieldFeedback = data.fieldFeedback ? { ...normalizeFieldFeedback(data.fieldFeedback), key: fieldKey } : null;
    const coherence = validOverview(data.coherence) ? normalizeOverview(data.coherence) : null;
    return { values: toLocalFieldKeys(data.submission), fields, fieldFeedback, coherence, progress: data.progress };
}

// 사례개념화 응답의 기본 형식(submission, fields)을 확인한다.
export function validConceptualization(data) {
    return Boolean(data?.submission && typeof data.submission === 'object' && data.fields && typeof data.fields === 'object');
}

// 사례개념화 API 오류를 화면 문구로 바꾼다. 오류 코드는 문구를 고르는 데만 쓴다.
function conceptualizationErrorMessage(error) {
    const code = error.response?.data?.code || error.response?.data?.error;
    if (!CONCEPTUALIZATION_ERROR_CODES[code]) return apiErrorMessage(error, CONCEPTUALIZATION_ERROR_MESSAGES);
    if (process.env.NODE_ENV !== 'production') console.error(error);
    return CONCEPTUALIZATION_ERROR_CODES[code];
}

// 사례개념화 API 주소.
function conceptualizationUrl(id) {
    return `/api/ai-clients/instances/${encodeURIComponent(id)}/analysis/conceptualization`;
}

// 사례개념화 항목 하나를 저장하고 그 항목의 AI 피드백을 받는다. field는 화면 항목 키.
export async function submitConceptualizationField(id, token, { field, text }) {
    let data;
    try {
        ({ data } = await authApi.post(conceptualizationUrl(id), { field: CONCEPTUALIZATION_FIELD_KEYS[field], text }, {
            headers: { 'X-Instance-Token': token },
            // 분석에 시간이 걸릴 수 있어 기본보다 길게 기다린다.
            timeout: 120000,
        }));
    } catch (error) {
        throw new Error(conceptualizationErrorMessage(error));
    }
    if (!validConceptualization(data) || !validFieldFeedback(data.fieldFeedback)) {
        if (process.env.NODE_ENV !== 'production') console.error('사례개념화 피드백 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return toConceptualizationResult(data);
}

// 저장된 사례개념화(항목 글과 항목별 피드백)를 불러온다. 한 번도 저장하지 않았으면 null.
export async function getConceptualization(id, token) {
    let data;
    try {
        ({ data } = await authApi.get(conceptualizationUrl(id), { headers: { 'X-Instance-Token': token } }));
    } catch (error) {
        if (error.response?.status === 404 && error.response.data?.code === 'ANALYSIS_NOT_FOUND') return null;
        throw new Error(conceptualizationErrorMessage(error));
    }
    if (!validConceptualization(data)) {
        if (process.env.NODE_ENV !== 'production') console.error('사례개념화 조회 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return toConceptualizationResult(data);
}

// 값이 문자열 배열인지 확인한다.
function isStringList(value) {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
}

// 전체 보기 점검 응답 형식을 확인한다. gaps는 없어도 된다.
function validOverview(data) {
    return typeof data?.summary === 'string' && Array.isArray(data.checks)
        && data.checks.every(check => typeof check?.key === 'string' && typeof check.label === 'string' && typeof check.note === 'string')
        && (data.gaps === undefined || isStringList(data.gaps));
}

// 연결 점검 결과를 화면에서 쓰는 형태로 맞춘다.
function normalizeOverview(data) {
    return normalizeOverview(data);
}

// 서버에 저장된 항목들이 서로 매끄럽게 이어지는지 AI 점검을 받는다.
export async function requestConceptualizationOverview(id, token) {
    let data;
    try {
        ({ data } = await authApi.post(`${conceptualizationUrl(id)}/overview`, {}, {
            headers: { 'X-Instance-Token': token },
            // 분석에 시간이 걸릴 수 있어 기본보다 길게 기다린다.
            timeout: 120000,
        }));
    } catch (error) {
        throw new Error(conceptualizationErrorMessage(error));
    }
    if (!validOverview(data)) {
        if (process.env.NODE_ENV !== 'production') console.error('사례개념화 전체 점검 응답 형식 오류', data);
        throw new Error(SERVER_ERROR_MESSAGE);
    }
    return { summary: data.summary, checks: data.checks, gaps: data.gaps || [] };
}
